/**
 * Shared HTTP client helpers for all AskIAM API modules.
 * Every domain-specific API file (clearance.ts, helpdesk.ts, etc.) should
 * import these instead of duplicating fetch wrappers.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Parsed JSON response body, when the error response was JSON. */
    public readonly body?: unknown,
    /** Server-requested retry delay, parsed from Retry-After when present. */
    public readonly retryAfterMs?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Re-entry guard so concurrent 401s from a stale session only trigger one redirect.
let redirectingToLogin = false;

/**
 * The auth cookie is sliding 8h. If the user comes back after the cookie
 * expired, the SPA bundle is still loaded and AuthContext's load-time check
 * has already passed, so individual API calls start 401'ing inline.
 * This interceptor catches any 401 from a `/api/*` call and bounces the
 * browser through the server-side OIDC login so the user lands back where
 * they were with a fresh cookie.
 */
function handleUnauthorized(): void {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  const returnUrl = window.location.pathname + window.location.search + window.location.hash;
  window.location.href = '/api/auth/login?returnUrl=' + encodeURIComponent(returnUrl);
}

/**
 * Maps an HTTP status to a clean, user-safe message. Used when the error body is not JSON
 * (e.g. an HTML gateway/timeout page) so we never surface raw markup in the UI.
 */
function friendlyStatusMessage(status: number, statusText?: string): string {
  if (status === 408 || status === 504) return 'The server took too long to respond. Please try again.';
  if (status === 502 || status === 503) return 'The service is temporarily unavailable. Please try again in a moment.';
  if (status >= 500) return 'The server encountered an unexpected error. Please try again.';
  if (status === 0) return 'Network error — please check your connection and try again.';
  return statusText ? `Request failed (${status} ${statusText}).` : `Request failed (${status}).`;
}

/**
 * Extracts a human-readable error message from an API error response.
 * Handles RFC 7807 ProblemDetails ({ detail, title }), AskIAM legacy ({ error, message }),
 * and AYGA format ({ Message, Errors }). For non-JSON bodies (e.g. an HTML reverse-proxy /
 * timeout error page) it returns a clean status-based message instead of the raw markup.
 */
async function extractError(res: Response): Promise<{ message: string; body?: unknown }> {
  const text = await res.text().catch(() => '');
  if (!text) return { message: friendlyStatusMessage(res.status, res.statusText) };

  const contentType = res.headers.get('content-type') ?? '';
  const trimmed = text.trimStart();
  const looksJson = contentType.includes('json') || trimmed.startsWith('{') || trimmed.startsWith('[');

  if (looksJson) {
    try {
      const json = JSON.parse(text);
      // RFC 7807 ProblemDetails / AskIAM legacy / AYGA format
      const message = json.detail ?? json.title
        ?? (typeof json.error === 'object' ? json.error?.message : json.error)
        ?? json.message ?? json.Message;
      if (typeof message === 'string' && message.trim()) {
        return { message, body: json };
      }
      return { message: friendlyStatusMessage(res.status, res.statusText), body: json };
    } catch {
      // Not actually JSON — fall through to the friendly generic message.
    }
  }

  // Non-JSON body (HTML error page, plain text, etc.): never surface raw markup to the user.
  return { message: friendlyStatusMessage(res.status, res.statusText) };
}

function parseRetryAfterMs(res: Response): number | undefined {
  const value = res.headers.get('Retry-After');
  if (!value) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1_000;
  const date = Date.parse(value);
  return Number.isNaN(date) ? undefined : Math.max(0, date - Date.now());
}

/** Parse an already-issued request with the shared safe-error and session handling. */
export async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    if (res.status === 401) {
      handleUnauthorized();
    }
    const { message, body } = await extractError(res);
    throw new ApiError(res.status, message, body, parseRetryAfterMs(res));
  }
  return res.json();
}

export async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' });
  return handleResponse<T>(res);
}

/**
 * A response plus the metadata that could not travel inside it.
 *
 * `authorizationDegraded` comes from the `X-Authorization-Degraded` response header. It is a
 * header rather than a field in the body because these endpoints return JSON **arrays**, which
 * have nowhere to put a flag — and wrapping them in an envelope would change a wire shape that
 * the server itself re-parses as an array in three places.
 */
export interface WithMeta<T> {
  data: T;
  /** True when the server could not check every team, so the payload may be incomplete. */
  authorizationDegraded: boolean;
}

export async function fetchJsonWithMeta<T>(url: string): Promise<WithMeta<T>> {
  const res = await fetch(url, { credentials: 'same-origin' });
  const degraded = res.headers.get('X-Authorization-Degraded') === 'true';
  const data = await handleResponse<T>(res);
  return { data, authorizationDegraded: degraded };
}

// ── Response cache ───────────────────────────────────────────────────────────
// The manage surfaces mount one `fetch` per tab with no coordination, so moving between tabs
// re-hit the API for data that had not changed. Telemetry showed the backend was not the
// constraint (Cosmos p50 1.5 ms), so the fix is to stop issuing the redundant calls rather than
// to make them faster.

interface CacheEntry {
  expiresAt: number;
  value: unknown;
}

const responseCache = new Map<string, CacheEntry>();
/** Requests that have been issued but not yet settled, so concurrent callers can share one. */
const inFlight = new Map<string, Promise<unknown>>();

export const DEFAULT_CACHE_TTL_MS = 60_000;

/**
 * GET + parse JSON, served from an in-memory cache for `ttlMs` and de-duplicated while in flight.
 *
 * Only successful responses are cached — a rejection leaves nothing behind, so the next caller
 * retries rather than replaying an error. Cached values are shared by reference, so callers must
 * treat the result as immutable (copy before sorting or mutating).
 */
export function cachedFetchJson<T>(url: string, ttlMs: number = DEFAULT_CACHE_TTL_MS): Promise<T> {
  return cachedGet<T>(url, url, ttlMs, fetchJson);
}

/**
 * `cachedFetchJson` for responses whose header metadata matters. The metadata is cached with the
 * body, so a cache hit cannot report a stale payload as freshly-verified.
 */
export function cachedFetchJsonWithMeta<T>(url: string, ttlMs: number = DEFAULT_CACHE_TTL_MS): Promise<WithMeta<T>> {
  return cachedGet<WithMeta<T>>(META_KEY_PREFIX + url, url, ttlMs, fetchJsonWithMeta);
}

/**
 * The two cached variants store different shapes — `T` and `WithMeta<T>` — so they must not share a
 * cache slot. Keyed on the URL alone, whichever ran first would seed the entry and the other would
 * cast it to its own type: an array where `{ data, authorizationDegraded }` was expected, or the
 * reverse. Nothing would throw at the cache; it would surface much later as a bad render.
 *
 * The prefix is stripped for invalidation, so `invalidateCached('/api/x')` still clears both.
 */
const META_KEY_PREFIX = 'meta\u0000';

function cachedGet<T>(
  key: string, url: string, ttlMs: number, fetcher: (u: string) => Promise<T>,
): Promise<T> {
  const hit = responseCache.get(key);
  if (hit && hit.expiresAt > Date.now()) {
    return Promise.resolve(hit.value as T);
  }

  // Independent of TTL: two tabs mounting at once must not each open a request.
  const pending = inFlight.get(key);
  if (pending) return pending as Promise<T>;

  const request = fetcher(url)
    .then((value) => {
      // Dropped by an invalidation that landed while this was in flight; the response is already
      // known to be stale, so return it to this caller but do not seed the cache with it.
      if (inFlight.get(key) === request) {
        responseCache.set(key, { expiresAt: Date.now() + ttlMs, value });
      }
      return value;
    })
    .finally(() => {
      if (inFlight.get(key) === request) inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
}

/** The URL a cache key was built from, whichever variant wrote it. */
function keyUrl(key: string): string {
  return key.startsWith(META_KEY_PREFIX) ? key.slice(META_KEY_PREFIX.length) : key;
}

/**
 * Drops every cached entry whose URL starts with `prefix`. Call after any mutation that could
 * change the affected reads — matching on a prefix means query-string variants
 * (`?includeInactive=true`) are covered by one call.
 *
 * In-flight requests are dropped too, so a read that started before the mutation cannot seed the
 * cache with a pre-mutation response.
 */
export function invalidateCached(prefix: string): void {
  for (const key of responseCache.keys()) {
    if (keyUrl(key).startsWith(prefix)) responseCache.delete(key);
  }
  for (const key of inFlight.keys()) {
    if (keyUrl(key).startsWith(prefix)) inFlight.delete(key);
  }
}

/** Drops the entire response cache. Intended for sign-out and explicit full-refresh actions. */
export function clearCachedResponses(): void {
  responseCache.clear();
  inFlight.clear();
}

export async function postJson<T>(
  url: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
    signal,
  });
  return handleResponse<T>(res);
}

export async function putJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

export async function patchJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

export async function del(url: string): Promise<void> {
  const res = await fetch(url, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) {
    if (res.status === 401) {
      handleUnauthorized();
    }
    const { message, body } = await extractError(res);
    throw new ApiError(res.status, message, body, parseRetryAfterMs(res));
  }
}
